import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BlankWhiteLayout from 'src/layouts/BlankWhiteLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotification } from 'src/contexts/NotificationContext';
import BackArrowHeader from 'src/components/Header/BackArrowHeader';
import { useCheckFamilyCode } from 'src/queries/family_code';
import Button from 'src/components/common/designSystem/Button';
import Footer from 'src/components/common/designSystem/Footer';
import { AutoFloatingPanel } from 'src/components/common/AutoFloatingPanel';

const FamilyCodePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showHelpPanel, setShowHelpPanel] = useState(false);

  const { mutateAsync: checkFamilyCodeMutation, isLoading: isCheckLoading } =
    useCheckFamilyCode();

  // Combined loading state
  const isLoading = isCheckLoading;

  // Focus on the first input when the component mounts
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInputChange = (index: number, value: string) => {
    const upperValue = value.toUpperCase();
    if (upperValue.length > 1) {
      // If pasting multiple characters, distribute them across inputs
      const chars = upperValue.toUpperCase().split('');
      const newCode = [...code];

      chars.forEach((char, charIndex) => {
        const targetIndex = index + charIndex;
        if (targetIndex < 6 && /^[A-Z0-9]$/.test(char)) {
          newCode[targetIndex] = char;
        }
      });

      setCode(newCode);

      // Focus on the next empty input or the last one
      const nextEmptyIndex = newCode.findIndex(c => c === '');
      if (nextEmptyIndex !== -1 && inputRefs.current[nextEmptyIndex]) {
        inputRefs.current[nextEmptyIndex].focus();
      } else if (inputRefs.current[5]) {
        inputRefs.current[5].focus();
      }
    } else if (/^[A-Z0-9]$/.test(upperValue) || upperValue === '') {
      // Handle single character input
      const newCode = [...code];
      newCode[index] = upperValue;
      setCode(newCode);

      // Move to next input if a character was entered
      if (upperValue && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current input is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      // Move to previous input on left arrow
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      // Move to next input on right arrow
      inputRefs.current[index + 1]?.focus();
    }
  };

  const verifyFamilyCode = async () => {
    const familyCode = code.join('');
    if (familyCode.length !== 6) {
      showError({
        message: t('family.code.incomplete'),
      });
      return;
    }

    try {
      // If user is not logged in, just check if the code is valid
      const result = await checkFamilyCodeMutation({
        join_code: familyCode,
      });

      if (result.exists) {
        // Add family code to search params
        const newParams = new URLSearchParams(searchParams);
        newParams.set('family_code', familyCode);

        const userType = searchParams.get('user_type');

        // If the user is a child, redirect to family user selection
        if (userType === 'child') {
          navigate(`/select-family-user?${newParams.toString()}`);
        } else if (userType === 'parent') {
          // If the user is a parent, go to the congratulation page
          navigate(`/family-code-congrats?${newParams.toString()}`);
        } else {
          // If no user type, go back to type selection with family code
          navigate(`/onboarding/select-type?${newParams.toString()}`);
        }

        showSuccess({
          message: t('family.code.valid'),
        });
      } else {
        showError({
          message: t('family.code.invalid'),
        });
      }
    } catch (error) {
      showError({
        error,
        message: t('family.code.invalid'),
      });
    }
  };

  return (
    <BlankWhiteLayout>
      <BackArrowHeader />
      <div className="flex flex-col items-center justify-center px-6 py-10 text-primary flex-1 gap-8">
        <div className="font-bold text-xl text-center">
          {t('family.code.title')}
        </div>

        <div className="flex justify-center gap-2">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => (inputRefs.current[index] = el)}
              type="text"
              value={digit}
              onChange={e => handleInputChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              className="w-12 h-14 bg-primary-lighter focus:bg-primary-light border-primary-light border-2 rounded-lg text-center text-xl font-bold text-primary uppercase"
              maxLength={1}
              inputMode="text"
              autoCapitalize="characters"
              data-testid={`family-code-input-${index}`}
            />
          ))}
        </div>

        <Button
          type="link"
          className="text-base !text-structural-darkest"
          onClick={() => setShowHelpPanel(true)}
          data-testid="help-family-code-button"
        >
          {t('family.code.helpButton')}
        </Button>
      </div>
      <Footer>
        <Button
          type="primary"
          onClick={verifyFamilyCode}
          loading={isLoading}
          disabled={isLoading || code.some(c => c === '')}
          data-testid="join-family-button"
        >
          {t('family.code.submit')}
        </Button>
      </Footer>

      <AutoFloatingPanel
        isOpen={showHelpPanel}
        onClose={() => setShowHelpPanel(false)}
        data-testid="help-family-code-panel"
      >
        <div className="p-6">
          <h2 className="font-bold text-lg mb-4 text-primary">
            {t('family.code.helpTitle')}
          </h2>
          <p className="mb-6">{t('family.code.helpDescription')}</p>
          <Button type="primary" onClick={() => setShowHelpPanel(false)} block>
            {t('global.understand')}
          </Button>
        </div>
      </AutoFloatingPanel>
    </BlankWhiteLayout>
  );
};

export default FamilyCodePage;
